/**
 * IndexedDB utility for storing audio paths during interview process
 * Audio paths are stored without extensions for obfuscation
 */

const DB_NAME = 'InterviewAudioDB';
const DB_VERSION = 1;
const STORE_NAME = 'audioPaths';

interface AudioStorage {
  jobId: string; // Using jobId as key instead of interviewLinkId for easier retrieval during application
  audioPaths: string[]; // Array of paths without extensions
  updatedAt: number;
}

/**
 * Initialize IndexedDB
 */
function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      
      // Create object store if it doesn't exist
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'jobId' });
      }
    };
  });
}

/**
 * Save a new audio path for a job application (upsert - avoids duplicates)
 */
export async function addAudioPath(jobId: string, audioPath: string): Promise<void> {
  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    // Get existing data
    const getRequest = store.get(jobId);
    
    return new Promise((resolve, reject) => {
      getRequest.onsuccess = () => {
        const existing = getRequest.result as AudioStorage | undefined;
        
        // Avoid duplicates - only add if path doesn't already exist
        const existingPaths = existing?.audioPaths || [];
        const pathExists = existingPaths.includes(audioPath);
        
        const data: AudioStorage = {
          jobId,
          audioPaths: pathExists ? existingPaths : [...existingPaths, audioPath],
          updatedAt: Date.now(),
        };

        const putRequest = store.put(data);
        
        putRequest.onsuccess = () => {
          if (!pathExists) {
          console.log(`✅ Saved audio path to IndexedDB for job ${jobId}: ${audioPath}`);
          } else {
            console.log(`ℹ️ Audio path already exists in IndexedDB for job ${jobId}: ${audioPath}`);
          }
          resolve();
        };
        putRequest.onerror = () => reject(putRequest.error);
      };
      
      getRequest.onerror = () => reject(getRequest.error);
    });
  } catch (error) {
    console.error('Error saving audio path to IndexedDB:', error);
    throw error;
  }
}

/**
 * Get all audio paths for a job application
 */
export async function getAudioPaths(jobId: string): Promise<string[]> {
  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(jobId);

    return new Promise((resolve, reject) => {
      request.onsuccess = () => {
        const result = request.result as AudioStorage | undefined;
        resolve(result?.audioPaths || []);
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error getting audio paths from IndexedDB:', error);
    return [];
  }
}

/**
 * Clear audio paths for a job application (after successful application submission)
 */
export async function clearAudioPaths(jobId: string): Promise<void> {
  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.delete(jobId);

    return new Promise((resolve, reject) => {
      request.onsuccess = () => {
        console.log(`🗑️ Cleared audio paths for job: ${jobId}`);
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error clearing audio paths from IndexedDB:', error);
    throw error;
  }
}

/**
 * Get count of stored audio paths for a job application
 */
export async function getAudioPathCount(jobId: string): Promise<number> {
  const paths = await getAudioPaths(jobId);
  return paths.length;
}
